// @flow
import type {Node} from 'react'
import React, {Fragment, useEffect} from 'react'
import {LocalQueryRenderer, graphql} from 'react-relay'
import flash from 'utils/MiscUtils/flash'
import {deleteAllNotifications} from 'utils/localNotifications'

import environment from 'environment'
import CollapsibleContainer from './CollapsibleContainer'
import FlashNotification from './FlashNotification'
import consumeServerSideNotifications from './consumeServerSideNotifications'
import type {
  FlashNotificationsQueryVariables as Variables,
  FlashNotificationsQueryResponse as Response,
} from './__generated__/FlashNotificationsQuery.graphql'

const variables: Variables = Object.freeze({})

type RendererProps = $ReadOnly<{
  // eslint-disable-next-line react/no-unused-prop-types
  props: ?Response,
  ...
}>

const query = graphql`
  query FlashNotificationsQuery {
    ... on Query { __typename }

    localNotifications {
      id
      placement
      ...FlashNotification_notification
    }
  }
`

function FlashNotifications(): Node {
  useEffect(() => {
    consumeServerSideNotifications().forEach(({alert, message}) => {
      if (alert)
        flash.danger(message)
      else
        flash.notice(message)
    })

    // This island unmounts when Turbo replaces <body> for a visit. The
    // notifications it was showing stay in the Relay store otherwise, and the
    // island shows them again when it mounts on the next page.
    return deleteAllNotifications
  }, [])

  return (
    <LocalQueryRenderer
      environment={environment}
      query={query}
      render={({props: rendererProps}: RendererProps) => {
        const localNotifications = rendererProps?.localNotifications || []
        const notifications = (placement) => localNotifications.filter(Boolean)
          .filter((notification) => notification.placement === placement)

        const placements = ['top', 'bottom']
        const renderNotifications = (placement) => (
          <CollapsibleContainer key={placement} placement={placement}>
            {notifications(placement.toUpperCase()).map((notification) => (
              <FlashNotification
                key={notification.id}
                notification={notification}
              />
            ))}
          </CollapsibleContainer>
        )

        return (
          <Fragment>
            {placements.map((placement) => renderNotifications(placement))}
          </Fragment>
        )
      }}
      variables={variables}
    />
  )
}

export default FlashNotifications
